import { useDispatch } from "react-redux";
import { createApi } from "@reduxjs/toolkit/query/react";
import { OracleQuoteInfo } from "@bridgesplit/abf-sdk";
import { ENDURANCE_API, getUnixTs } from "@bridgesplit/utils";

import { ROUT_FETCH_ORACLE_HISTORICAL_PRICES_ROUTE, FETCH_ORACLE_PRICES_ROUTE } from "../constants";
import { abfSerializeQueryArgs, marketsPublicBaseQuery, unauthenticatedSerializeQueryArgs } from "./util";
import { OHLCFilter, OracleOHLC } from "../types/oracle";

const ORACLE_PRICES = "OraclePrices";
const ENDURANCE_API_KEY = process.env.ENDURANCE_API_KEY ?? "582bc8cd-e962-4f30-8d7c-c731d54eb669";

type OraclePriceStreamMessage = {
    type?: string;
    data?: OraclePriceStreamItem[];
};

type OraclePriceStreamItem = {
    mint?: string;
    spotPrice: number;
    twapPrice?: number;
};

export const oraclePricesApi = createApi({
    reducerPath: "oraclePricesApi",
    baseQuery: marketsPublicBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,
    tagTypes: [ORACLE_PRICES],
    refetchOnMountOrArgChange: 5,
    endpoints: (builder) => ({
        oraclePrices: builder.query<Record<string, OracleQuoteInfo>, void>({
            serializeQueryArgs: unauthenticatedSerializeQueryArgs,
            providesTags: [ORACLE_PRICES],
            query: () => FETCH_ORACLE_PRICES_ROUTE,
            transformResponse: (rawData: Record<string, Omit<OracleQuoteInfo, "nowTimestamp">>) => {
                const now = getUnixTs();
                return Object.fromEntries(
                    Object.entries(rawData).map(([key, value]) => [key, { ...value, nowTimestamp: now }])
                );
            },
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }) {
                if (typeof window === "undefined" || !window.WebSocket) return;

                let ws: WebSocket | undefined;
                let pollInterval: NodeJS.Timeout | undefined;

                const startPolling = () => {
                    if (pollInterval) return;
                    pollInterval = setInterval(() => {
                        dispatch(oraclePricesApi.endpoints.oraclePrices.initiate(arg, { forceRefetch: true }));
                    }, 5000);
                };

                const stopPolling = () => {
                    if (pollInterval) {
                        clearInterval(pollInterval);
                        pollInterval = undefined;
                    }
                };

                try {
                    await cacheDataLoaded;

                    ws = new WebSocket(`wss://${ENDURANCE_API}/subscribe?api_key=${ENDURANCE_API_KEY}`);
                    if (!ws) throw new Error("WebSocket not supported");

                    ws.onopen = () => {
                        stopPolling();
                    };

                    ws.onerror = () => {
                        startPolling();
                    };

                    ws.onclose = () => {
                        startPolling();
                    };

                    ws.onmessage = (event: MessageEvent) => {
                        try {
                            let payload: OraclePriceStreamMessage;
                            try {
                                payload = JSON.parse(event.data);
                            } catch (error) {
                                return;
                            }

                            if (payload?.type !== "oracle_price_update" || !payload.data) return;

                            const now = getUnixTs();

                            updateCachedData((draft) => {
                                payload.data?.forEach((item) => {
                                    if (!item?.mint) return;

                                    const twapPrice = item.twapPrice ?? item.spotPrice;
                                    const existingQuote = draft[item.mint];

                                    if (existingQuote) {
                                        // The stream only carries spot/twap — marketPrice comes from the
                                        // REST response and must survive stream ticks
                                        existingQuote.spotPrice = item.spotPrice;
                                        existingQuote.twapPrice = twapPrice;
                                        existingQuote.nowTimestamp = now;
                                    } else {
                                        // marketPrice omitted: consumers fall back to usdPrice
                                        draft[item.mint] = {
                                            oracleAccount: item.mint,
                                            baseMint: item.mint,
                                            lastUpdateTime: now,
                                            spotPrice: item.spotPrice,
                                            twapPrice,
                                            uncertainty: 0,
                                            nowTimestamp: now
                                        };
                                    }
                                });
                            });
                        } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error("Failed to process oracle websocket update", error);
                        }
                    };

                    await cacheEntryRemoved;
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("Oracle prices websocket streaming failed", error);
                    startPolling();
                } finally {
                    stopPolling();
                    ws?.close();
                }
            }
        }),
        oracleHistoricalPrices: builder.query<Record<string, OracleOHLC[]>, OHLCFilter>({
            providesTags: [ORACLE_PRICES],
            query: (body) => ({
                url: ROUT_FETCH_ORACLE_HISTORICAL_PRICES_ROUTE,
                method: "POST",
                body
            }),
            keepUnusedDataFor: 7200, // 2 hours in seconds
            serializeQueryArgs: ({ queryArgs }) => ({
                mints: queryArgs.mints,
                startTimestamp: queryArgs.startTimestamp
            }),
            extraOptions: { maxRetries: 1 }
        })
    })
});

export const { useOraclePricesQuery, useOracleHistoricalPricesQuery } = oraclePricesApi;

export function useOraclePricesApi() {
    const dispatch = useDispatch();

    return {
        resetOraclePricesApi: () => dispatch(oraclePricesApi.util.invalidateTags([ORACLE_PRICES]))
    };
}
